import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Gift, Send, Lock, Paperclip, X, Loader2 } from "lucide-react";
import { api, formatErr, timeAgo, mediaSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import TipButton from "@/components/TipDialog";

function ConversationList({ active }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const load = () => api.get("/messages").then((r) => setItems(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    window.addEventListener("riff:messages-updated", load);
    return () => { clearInterval(t); window.removeEventListener("riff:messages-updated", load); };
  }, [active]);
  return (
    <div className="h-full overflow-y-auto" data-testid="conversation-list">
      <h1 className="border-b border-[#2A2E37] px-4 py-4 text-base font-semibold tracking-tight">Messages</h1>
      {items?.length === 0 && (
        <p className="px-4 py-8 text-sm text-gray-500" data-testid="conversations-empty">No messages yet. Open a creator's page and tap Message.</p>
      )}
      {items?.map((c) => (
        <Link key={c.user.id} to={`/messages/${c.user.username}`} data-testid={`conversation-${c.user.username}`}
          className={`flex items-center gap-3 border-b border-[#2A2E37] px-4 py-3 transition-colors hover:bg-white/5 ${active === c.user.username ? "bg-white/5" : ""}`}>
          <Avatar user={c.user} size="h-9 w-9" />
          <div className="min-w-0 flex-1">
            <p className="flex items-center justify-between gap-2 text-sm">
              <span className={`truncate ${c.unread ? "font-semibold text-white" : "font-medium text-gray-200"}`}>{c.user.name}</span>
              <span className="shrink-0 text-[11px] text-gray-600">{timeAgo(c.last_message.created_at)}</span>
            </p>
            <p className={`truncate text-xs ${c.unread && active !== c.user.username ? "text-gray-200" : "text-gray-500"}`}>{c.last_message.locked ? "Locked message" : c.last_message.text || (c.last_message.media_src ? "Photo / video" : "")}</p>
          </div>
          {c.unread > 0 && active !== c.user.username && <span className="h-2 w-2 shrink-0 rounded-full bg-[#FF2E4D]" data-testid="conversation-unread-dot" />}
        </Link>
      ))}
    </div>
  );
}

function Bubble({ m, mine, onUnlock, onMedia }) {
  if (m.locked) {
    return (
      <div className="flex justify-start" data-testid="message-bubble">
        <div className="w-64 rounded-md border border-[#FF2E4D]/30 bg-[#1C1F26] p-4" data-testid="locked-message">
          <div className="flex items-center gap-2 text-sm font-semibold"><Lock className="h-4 w-4 text-[#FF2E4D]" /> Locked message</div>
          <p className="mt-1 text-xs text-gray-400">{m.has_media ? "Includes a photo or video. " : ""}Unlock to read it.</p>
          <button onClick={() => onUnlock(m)} data-testid="unlock-message-btn"
            className="btn-neon mt-3 flex w-full items-center justify-center gap-1.5 py-2 text-xs font-semibold text-white">
            <Lock className="h-3.5 w-3.5" /> Unlock for ${m.price.toFixed(2)}
          </button>
          <p className="mt-2 text-[10px] text-gray-500">{timeAgo(m.created_at)}</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="message-bubble">
      <div className={`max-w-[78%] overflow-hidden rounded-md text-sm leading-relaxed ${mine ? "bg-[#FF2E4D] text-white" : "border border-[#2A2E37] bg-[#1C1F26] text-gray-100"}`}>
        {m.media_src && (m.media_type === "video"
          ? <video src={mediaSrc(m.media_src)} controls playsInline onLoadedMetadata={onMedia} className="max-h-80 w-full bg-black object-contain" data-testid="message-media" />
          : <img src={mediaSrc(m.media_src)} alt="" onLoad={onMedia} className="max-h-80 w-full object-cover" data-testid="message-media" />)}
        <div className="px-3.5 py-2">
          {m.tip_amount > 0 && (
            <p className={`mb-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider ${mine ? "text-white/80" : "text-[#FF2E4D]"}`} data-testid="message-tip-badge">
              <Gift className="h-3 w-3" /> Tip ${m.tip_amount.toFixed(2)}
            </p>
          )}
          {m.price > 0 && (
            <p className={`mb-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider ${mine ? "text-white/80" : "text-gray-400"}`} data-testid="message-paid-badge">
              <Lock className="h-3 w-3" /> Paid ${m.price.toFixed(2)} · {m.unlocked ? "Unlocked" : "Not unlocked yet"}
            </p>
          )}
          {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
          <p className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-gray-500"}`}>{timeAgo(m.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

function PaidComposer({ priceKey, setPriceKey, file, setFile, onClose }) {
  const [prices, setPrices] = useState([]);
  useEffect(() => { api.get("/unlock-prices").then((r) => setPrices(r.data)); }, []);
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-[#2A2E37] bg-[#0F1114] px-3 py-2.5" data-testid="paid-composer">
      <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[#FF2E4D]"><Lock className="h-3 w-3" /> Paid</span>
      {prices.map((p) => (
        <button key={p.lookup_key} type="button" onClick={() => setPriceKey(p.lookup_key)} data-testid={`paid-price-${p.lookup_key}`}
          className={`rounded-sm border px-2.5 py-1 text-xs tabular-nums transition-colors ${priceKey === p.lookup_key ? "border-white bg-white text-[#090A0C]" : "border-[#2A2E37] text-gray-300 hover:border-white/30"}`}>
          ${p.amount}
        </button>
      ))}
      <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-gray-300 hover:text-white">
        <Paperclip className="h-3.5 w-3.5" /> <span className="max-w-[120px] truncate">{file ? file.name : "Attach"}</span>
        <input type="file" accept="image/*,video/*" className="hidden" data-testid="paid-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <button type="button" onClick={onClose} aria-label="Close paid message" data-testid="paid-close-btn" className="p-1 text-gray-500 hover:text-white"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Chat({ username }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [paid, setPaid] = useState(false);
  const [priceKey, setPriceKey] = useState("unlock_500");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const count = data?.messages.length;
  const otherId = data?.user?.id;

  const seen = useRef(-1);
  const load = useCallback(() => api.get(`/messages/${username}`).then((r) => {
    setData(r.data);
    if (seen.current !== r.data.messages.length) {
      seen.current = r.data.messages.length;
      window.dispatchEvent(new Event("riff:messages-updated"));
    }
  }).catch((e) => setError(formatErr(e))), [username]);

  useEffect(() => {
    setData(null);
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onWs = (e) => {
      const d = e.detail;
      if (d.type === "message_unlocked") return load();
      if (d.type !== "message" || !otherId) return;
      const m = d.message;
      if (m.sender_id !== otherId && m.recipient_id !== otherId) return;
      setData((prev) => (prev && !prev.messages.some((x) => x.id === m.id) ? { ...prev, messages: [...prev.messages, m] } : prev));
      if (m.sender_id === otherId) load();
    };
    window.addEventListener("riff:ws", onWs);
    return () => window.removeEventListener("riff:ws", onWs);
  }, [otherId, load]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [count]);

  const append = (m) => setData((d) => (d.messages.some((x) => x.id === m.id) ? d : { ...d, messages: [...d.messages, m] }));

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !(paid && file)) return;
    setSending(true);
    try {
      if (paid) {
        const fd = new FormData();
        fd.append("lookup_key", priceKey);
        fd.append("text", text);
        if (file) fd.append("file", file);
        append((await api.post(`/messages/${username}/paid`, fd)).data);
        setPaid(false);
        setFile(null);
      } else {
        append((await api.post(`/messages/${username}`, { text })).data);
      }
      setText("");
      window.dispatchEvent(new Event("riff:messages-updated"));
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSending(false); }
  };

  const unlock = async (m) => {
    try {
      const { data: r } = await api.post("/payments/unlock", { message_id: m.id, origin_url: window.location.origin });
      localStorage.setItem("velvet_pending_creator", data.user.username);
      window.location.href = r.checkout_url;
    } catch (err) { toast.error(formatErr(err)); }
  };

  if (error) return <p className="p-10 text-center text-sm text-gray-400" data-testid="chat-error">{error}</p>;
  if (!data) return <p className="p-10 text-center text-sm text-gray-500">Loading...</p>;

  return (
    <div className="flex h-full flex-col" data-testid="chat-pane">
      <div className="flex items-center gap-3 border-b border-[#2A2E37] px-4 py-3">
        <Link to="/messages" className="rounded-sm p-1 text-gray-400 hover:text-white md:hidden" data-testid="chat-back-btn"><ArrowLeft className="h-4 w-4" /></Link>
        <Link to={`/u/${data.user.username}`} className="flex min-w-0 flex-1 items-center gap-3" data-testid="chat-user-link">
          <Avatar user={data.user} size="h-8 w-8" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{data.user.name}</p>
            <p className="text-xs text-gray-500">@{data.user.username}{data.user.is_subscribed ? " · Subscribed" : ""}</p>
          </div>
        </Link>
        <TipButton creator={data.user} className="!px-3 !py-1.5 !text-xs" />
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-5" data-testid="chat-messages">
        {data.messages.length === 0 && <p className="pt-10 text-center text-sm text-gray-500">Say hi to @{data.user.username}.</p>}
        {data.messages.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === user.id} onUnlock={unlock} onMedia={() => endRef.current?.scrollIntoView({ block: "end" })} />)}
        <div ref={endRef} />
      </div>
      {paid && <PaidComposer priceKey={priceKey} setPriceKey={setPriceKey} file={file} setFile={setFile} onClose={() => { setPaid(false); setFile(null); }} />}
      <form onSubmit={send} className="flex gap-2 border-t border-[#2A2E37] p-3">
        {user.is_creator && (
          <button type="button" onClick={() => setPaid((v) => !v)} aria-label="Paid message" data-testid="paid-toggle-btn"
            className={`rounded-sm border px-3 transition-colors ${paid ? "border-[#FF2E4D] bg-[#FF2E4D]/10 text-[#FF2E4D]" : "border-[#2A2E37] bg-white/5 text-gray-400 hover:text-white"}`}>
            <Lock className="h-4 w-4" />
          </button>
        )}
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} data-testid="chat-input"
          placeholder={paid ? "Write a paid message..." : "Write a message..."}
          className="flex-1 rounded-sm border border-[#2A2E37] bg-white/5 px-3.5 py-2.5 text-sm outline-none focus:border-white/40" />
        <button type="submit" disabled={sending} data-testid="chat-send-btn" aria-label="Send" className="btn-neon px-4 text-white disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}

export default function Messages() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (username && user && username.toLowerCase() === user.username) nav("/messages", { replace: true }); }, [username, user, nav]);
  return (
    <div className="mx-auto h-[calc(100dvh-7.5rem)] max-w-6xl md:h-[calc(100dvh-3.5rem)] md:px-8 md:py-6" data-testid="messages-page">
      <div className="grid h-full overflow-hidden border-[#2A2E37] bg-[#121418] md:grid-cols-[320px_minmax(0,1fr)] md:rounded-md md:border">
        <div className={`h-full border-r border-[#2A2E37] ${username ? "hidden md:block" : ""}`}><ConversationList active={username} /></div>
        <div className={`h-full min-h-0 ${username ? "" : "hidden md:block"}`}>
          {username ? <Chat username={username} /> : (
            <div className="hidden h-full items-center justify-center text-sm text-gray-500 md:flex">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
